import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { glossary, scoutingCenter } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial, thickMaterial } from '@sorare/core/src/style/utils';

import { AverageScores } from './AverageScores';
import { Breakdown } from './Breakdown';
import { FirstTime } from './FirstTime';
import { Prices } from './Prices';
import { Projections } from './Projections';
import { Stats } from './Stats';
import { StatsDisplayToggle } from './StatsDisplayToggle';
import { useManagePlayerSearchView } from './useManagePlayerSearchView';

const StyledDrawer = styled(Drawer)`
  width: 100%;
  height: 100%;
  ${regularMaterial}
  @media ${tabletAndAbove} {
    width: 350px;
    border-left: 1px solid var(--c-nd-150);
  }
`;

const Body = styled(Vertical)`
  position: relative;
  isolation: isolate;
  height: 100%;
`;
const Content = styled(Vertical).attrs({ gap: 2 })`
  flex-grow: 1;
  padding: var(--triple-unit) var(--triple-unit) var(--double-unit);
`;

const Header = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: calc(var(--unit) * 5) [title] 1fr [close] calc(
      var(--unit) * 5
    );
`;
const Title = styled(BodyM).attrs({ bold: true })`
  grid-area: title;
  text-align: center;
`;
const StyledCloseButton = styled(CloseButton)`
  grid-area: close;
`;

const Actions = styled(Vertical).attrs({ gap: 2 })`
  ${thickMaterial}
  position: sticky;
  bottom: 0;
  border-top: 1px solid var(--c-nd-150);
  padding: var(--triple-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const StatsManager = ({ open, onClose }: Props) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const { defaultStatsManager, updatePlayerSearchView } =
    useManagePlayerSearchView();

  const reset = () => {
    updatePlayerSearchView(defaultStatsManager);
  };

  return (
    <StyledDrawer
      open={!!open}
      side={isTabletOrAbove ? 'right' : 'bottom'}
      onBackdropClick={onClose}
      keepMounted
    >
      <Body>
        <Content>
          <Header>
            <Title>
              <FormattedMessage {...scoutingCenter.advancedView} />
            </Title>

            <StyledCloseButton onClose={onClose} />
          </Header>
          <FirstTime />
          <AverageScores />
          <Projections />
          <Breakdown />
          <HorizontalDivider />
          <StatsDisplayToggle />
          <Stats />
          <HorizontalDivider />
          <Prices />
        </Content>
        <Actions gap={2}>
          <Button size="medium" color="primary" onClick={onClose}>
            <FormattedMessage {...glossary.savePreferences} />
          </Button>
          <Button size="medium" color="tertiary" onClick={reset}>
            <FormattedMessage id="StatsManager.reset" defaultMessage="Reset" />
          </Button>
        </Actions>
      </Body>
    </StyledDrawer>
  );
};
